import { ProgressFeed } from "#src/routes/investigations/[investigationId]/ProgressFeed";
import { Button, Icon } from "@validereinc/common-components";
import { type InvestigationProgressEventType } from "@validereinc/domain";
import classNames from "classnames/bind";
import React, { useState } from "react";
import styles from "./InvestigationDetailPage.module.scss";

const cx = classNames.bind(styles);

const STARTING_PHASE = "Starting";
const STARTING_DETAIL = "Waiting for the agent to begin...";

export type ProgressTickerProps = {
  progress: InvestigationProgressEventType[];
};

const getLatestSummary = (
  progress: InvestigationProgressEventType[]
): { phase: string; detail: string } => {
  const latest = progress[progress.length - 1];
  if (!latest) return { phase: STARTING_PHASE, detail: STARTING_DETAIL };
  const phase =
    typeof latest.phase === "string" && latest.phase.length > 0
      ? latest.phase
      : "Working";
  const detail = typeof latest.detail === "string" ? latest.detail : "";
  return { phase, detail };
};

export const ProgressTicker = ({ progress }: ProgressTickerProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const { phase, detail } = getLatestSummary(progress);
  const stepCount = progress.length;
  const hasSteps = stepCount > 0;

  return (
    <div
      className={cx("progress-ticker")}
      data-testid="progress-ticker"
    >
      <div className={cx("progress-ticker__strip")}>
        <span
          className={cx("progress-feed__dot", "progress-feed__dot--latest")}
          aria-hidden
        />
        <div
          className={cx("progress-ticker__text")}
          data-testid="progress-ticker-summary"
        >
          <span className={cx("progress-ticker__phase")}>{phase}</span>
          {detail ? (
            <>
              <span
                className={cx("progress-ticker__separator")}
                aria-hidden
              >
                ·
              </span>
              <span
                className={cx("progress-ticker__detail")}
                title={detail}
              >
                {detail}
              </span>
            </>
          ) : null}
        </div>
        <Button
          variant="text"
          size="small"
          className={cx("progress-ticker__toggle")}
          slotRight={<Icon variant={isExpanded ? "caret-up" : "caret-down"} />}
          onClick={() => {
            setIsExpanded((open) => !open);
          }}
          aria-expanded={isExpanded}
          aria-controls="progress-ticker-timeline"
        >
          {isExpanded
            ? "Hide steps"
            : hasSteps
              ? `Show steps (${stepCount})`
              : "Show steps"}
        </Button>
      </div>
      {isExpanded ? (
        <div
          id="progress-ticker-timeline"
          className={cx("progress-ticker__timeline")}
        >
          <ProgressFeed progress={progress} />
        </div>
      ) : null}
    </div>
  );
};
